import { jsx, jsxs } from 'react/jsx-runtime';
import React__default, { forwardRef, useMemo, useCallback } from 'react';
import { useDebounce } from '@circlefin/hooks';
import classNames from 'classnames';
import { useCombobox, useMultipleSelection } from 'downshift';
import isEqual from 'lodash.isequal';
import { Input } from '../Input/Input.es.js';
import { MultiSelectComboboxInner } from './MultiSelectCombobox.Inner/MultiSelectCombobox.Inner.es.js';
import { MultiSelectComboboxOptions } from './MultiSelectCombobox.Options/MultiSelectCombobox.Options.es.js';
import { SelectedChips } from './SelectedChip/SelectedChips.es.js';

function MultiSelectComboboxCore({ name, items, searchTerm, error, warning, label, labelSuffix, display, placeholder, message, intent, className, debounce = 0, onChange, onBlur, disabled, filtered = items, selectedValues = [], menuLoading = false, inputLoading = false, dropDirection = 'down', onInputChange, getSelectedItemLabel, visibleSelectedCount, hideSelectedChips = false, onDebouncedInputChange, onClearInput, children, required, ...rest }, ref) {
    const debouncedOnInputChange = useDebounce({
        callback: (search, callback)=>callback(search),
        delay: debounce
    });
    const { getDropdownProps, removeSelectedItem, setSelectedItems, reset } = useMultipleSelection({
        selectedItems: selectedValues,
        defaultActiveIndex: 0,
        onStateChange (changes) {
            const { selectedItems: newSelectedValues, type } = changes;
            switch(type){
                case useMultipleSelection.stateChangeTypes.FunctionRemoveSelectedItem:
                case useMultipleSelection.stateChangeTypes.FunctionReset:
                case useMultipleSelection.stateChangeTypes.FunctionSetSelectedItems:
                case useMultipleSelection.stateChangeTypes.FunctionAddSelectedItem:
                    onChange?.(newSelectedValues ?? []);
                    break;
                case useMultipleSelection.stateChangeTypes.SelectedItemClick:
                case useMultipleSelection.stateChangeTypes.SelectedItemKeyDownDelete:
                case useMultipleSelection.stateChangeTypes.SelectedItemKeyDownBackspace:
                case useMultipleSelection.stateChangeTypes.SelectedItemKeyDownNavigationNext:
                case useMultipleSelection.stateChangeTypes.SelectedItemKeyDownNavigationPrevious:
                case useMultipleSelection.stateChangeTypes.DropdownKeyDownNavigationPrevious:
                case useMultipleSelection.stateChangeTypes.DropdownKeyDownBackspace:
                case useMultipleSelection.stateChangeTypes.DropdownClick:
                case useMultipleSelection.stateChangeTypes.FunctionSetActiveIndex:
                    break;
            }
        }
    });
    const selectAllOptionSelected = useMemo(()=>{
        // Returns the Select All item if it exists and is selected.
        return items.find((item)=>{
            if (item.selectAll) {
                return selectedValues.some((selectedValue)=>isEqual(selectedValue, item.value));
            }
            return false;
        });
    }, [
        items,
        selectedValues
    ]);
    const getItemByValue = useCallback((value)=>{
        return items.find((item)=>isEqual(item.value, value));
    }, [
        items
    ]);
    const isItemSelected = useCallback((item)=>{
        return selectedValues.some((selectedValue)=>{
            return isEqual(selectedValue, item.value);
        });
    }, [
        selectedValues
    ]);
    const getEqualSelectedValue = useCallback((value)=>{
        return selectedValues.find((selectedValue)=>isEqual(selectedValue, value));
    }, [
        selectedValues
    ]);
    const toggleSelected = useCallback((item)=>{
        // If the item is already selected, we deselect it.
        if (isItemSelected(item)) {
            removeSelectedItem(getEqualSelectedValue(item.value));
            return;
        }
        // If the item the user wants to select is a "Select All" option,
        // we need to select it and deselect all the other items.
        if (item.selectAll) {
            setSelectedItems([
                item.value
            ]);
            return;
        }
        // If the item the user wants to select is a regular item,
        // we need to deselect any previous "Select All" item, keep the
        // already selected items, and add the new item as selected.
        setSelectedItems([
            ...selectedValues.filter((selectedValue)=>!getItemByValue(selectedValue)?.selectAll),
            item.value
        ]);
    }, [
        isItemSelected,
        setSelectedItems,
        selectedValues,
        removeSelectedItem,
        getEqualSelectedValue,
        getItemByValue
    ]);
    const getFilteredItemIndex = useCallback((selectedItem)=>{
        return filtered.findIndex((item)=>isEqual(item.value, selectedItem?.value));
    }, [
        filtered
    ]);
    const getValidHighlightIndex = useCallback((desiredIndex, type)=>{
        // If desiredIndex is not a valid one to start (e.g. undefined or -1),
        // or if the desiredIndex item is not a header (most of the times),
        // we accept it.
        if (desiredIndex == null) {
            return desiredIndex;
        }
        const desiredItem = filtered[desiredIndex];
        if (desiredItem == null || !desiredItem.header) {
            return desiredIndex;
        }
        let index = desiredIndex;
        do {
            switch(type){
                case useCombobox.stateChangeTypes.InputKeyDownArrowDown:
                case useCombobox.stateChangeTypes.InputKeyDownHome:
                    // Move down, wrapping around
                    index = (index + 1) % filtered.length;
                    break;
                case useCombobox.stateChangeTypes.InputKeyDownArrowUp:
                case useCombobox.stateChangeTypes.InputKeyDownEnd:
                    // Move up, wrapping around
                    index = (index - 1 + filtered.length) % filtered.length;
                    break;
            }
            // Break if we find a non-header item
            if (filtered.at(index) != null && !filtered[index].header) {
                return index;
            }
        // If we've come back to the start index, break out
        }while (index !== desiredIndex)
    }, [
        filtered
    ]);
    const selectedItems = useMemo(()=>{
        return selectedValues.map((selectedValue)=>getItemByValue(selectedValue)).filter((selectedItem)=>selectedItem != null);
    }, [
        getItemByValue,
        selectedValues
    ]);
    const { isOpen, inputValue: internalInputValue, toggleMenu, getLabelProps, getMenuProps, getItemProps, getInputProps, getComboboxProps, highlightedIndex, setInputValue: setInternalInputValue } = useCombobox({
        items: filtered,
        inputValue: searchTerm,
        defaultHighlightedIndex: 0,
        selectedItem: null,
        onStateChange: ({ selectedItem, type, inputValue })=>{
            switch(type){
                case useCombobox.stateChangeTypes.InputChange:
                case useCombobox.stateChangeTypes.FunctionSetInputValue:
                    if (!inputValue && selectAllOptionSelected) {
                        reset();
                    }
                    break;
                case useCombobox.stateChangeTypes.ItemClick:
                case useCombobox.stateChangeTypes.InputKeyDownEnter:
                    if (selectedItem) {
                        toggleSelected(selectedItem);
                    }
                    // once a user selects an item using enter key, clear input value
                    onInputChange('');
                    if (onDebouncedInputChange != null) {
                        debouncedOnInputChange('', onDebouncedInputChange);
                    }
                    break;
                case useCombobox.stateChangeTypes.InputKeyDownArrowDown:
                case useCombobox.stateChangeTypes.InputKeyDownArrowUp:
                case useCombobox.stateChangeTypes.InputKeyDownEscape:
                case useCombobox.stateChangeTypes.InputKeyDownHome:
                case useCombobox.stateChangeTypes.InputKeyDownEnd:
                case useCombobox.stateChangeTypes.InputBlur:
                case useCombobox.stateChangeTypes.MenuMouseLeave:
                case useCombobox.stateChangeTypes.ItemMouseMove:
                case useCombobox.stateChangeTypes.ToggleButtonClick:
                case useCombobox.stateChangeTypes.FunctionToggleMenu:
                case useCombobox.stateChangeTypes.FunctionOpenMenu:
                case useCombobox.stateChangeTypes.FunctionCloseMenu:
                case useCombobox.stateChangeTypes.FunctionSetHighlightedIndex:
                case useCombobox.stateChangeTypes.FunctionSelectItem:
                case useCombobox.stateChangeTypes.FunctionReset:
                case useCombobox.stateChangeTypes.ControlledPropUpdatedSelectedItem:
                    break;
            }
        },
        itemToString: (item)=>item?.label ?? '',
        stateReducer: (state, actionAndChanges)=>{
            const { type, changes } = actionAndChanges;
            const shouldKeepMenuOpen = Boolean(changes.selectedItem);
            switch(type){
                case useCombobox.stateChangeTypes.InputChange:
                case useCombobox.stateChangeTypes.FunctionSetInputValue:
                    // Clear input
                    if (!changes.inputValue) {
                        return {
                            ...changes,
                            inputValue: '',
                            selectedItem: null
                        };
                    }
                    return changes;
                case useCombobox.stateChangeTypes.InputKeyDownEnter:
                    {
                        // reset input value input once user selects an item
                        return {
                            ...changes,
                            inputValue: '',
                            // After selecting an item, we should keep the menu open
                            // and prevent downshift from resetting the highlight to the first item.
                            highlightedIndex: getFilteredItemIndex(changes.selectedItem),
                            isOpen: shouldKeepMenuOpen
                        };
                    }
                case useCombobox.stateChangeTypes.ItemClick:
                case useCombobox.stateChangeTypes.InputBlur:
                    {
                        return {
                            ...changes,
                            // After selecting an item, we should keep the menu open
                            // and prevent downshift from resetting the highlight to the first item.
                            highlightedIndex: getFilteredItemIndex(changes.selectedItem),
                            isOpen: shouldKeepMenuOpen
                        };
                    }
                case useCombobox.stateChangeTypes.ControlledPropUpdatedSelectedItem:
                    {
                        // internalInputValue is derived from props
                        return {
                            ...changes,
                            inputValue: state.inputValue
                        };
                    }
                case useCombobox.stateChangeTypes.InputKeyDownArrowDown:
                case useCombobox.stateChangeTypes.InputKeyDownArrowUp:
                case useCombobox.stateChangeTypes.InputKeyDownEnd:
                case useCombobox.stateChangeTypes.InputKeyDownHome:
                    {
                        return {
                            ...changes,
                            // We should skip header items
                            highlightedIndex: getValidHighlightIndex(changes.highlightedIndex, type)
                        };
                    }
                case useCombobox.stateChangeTypes.InputKeyDownEscape:
                case useCombobox.stateChangeTypes.MenuMouseLeave:
                case useCombobox.stateChangeTypes.ItemMouseMove:
                case useCombobox.stateChangeTypes.ToggleButtonClick:
                case useCombobox.stateChangeTypes.FunctionToggleMenu:
                case useCombobox.stateChangeTypes.FunctionOpenMenu:
                case useCombobox.stateChangeTypes.FunctionCloseMenu:
                case useCombobox.stateChangeTypes.FunctionSetHighlightedIndex:
                case useCombobox.stateChangeTypes.FunctionSelectItem:
                case useCombobox.stateChangeTypes.FunctionReset:
                    return changes;
            }
        }
    });
    const handleClearInput = useCallback(()=>{
        setInternalInputValue('');
        onClearInput?.();
    }, [
        onClearInput,
        setInternalInputValue
    ]);
    const multiSelectComboboxClasses = classNames('cb-multiselect-combobox', {
        focused: isOpen,
        error: error,
        disabled,
        'no-label': !label,
        'input-loading': inputLoading
    }, className);
    const menuContainerCss = classNames('multi-select-combobox-menu-container', `drop-direction-${dropDirection}`, {
        open: isOpen
    });
    const showSelectAllOption = useMemo(()=>selectAllOptionSelected && !internalInputValue && !hideSelectedChips, [
        selectAllOptionSelected,
        internalInputValue,
        hideSelectedChips
    ]);
    const handleInputChange = useCallback((e)=>{
        const inputValue = e.target.value;
        onInputChange(inputValue ?? '');
        if (onDebouncedInputChange != null) {
            debouncedOnInputChange(inputValue ?? '', onDebouncedInputChange);
        }
    }, [
        onInputChange,
        onDebouncedInputChange,
        debouncedOnInputChange
    ]);
    return jsx("div", {
        ...getComboboxProps(),
        "aria-required": required,
        className: multiSelectComboboxClasses,
        "data-testid": "multiselect-combobox-container",
        title: label,
        ...rest,
        children: jsxs(Input, {
            disabled: disabled,
            display: display,
            emphasize: intent === 'filter',
            error: error,
            label: label,
            labelProps: getLabelProps(),
            labelSuffix: labelSuffix,
            loading: inputLoading,
            message: message,
            placeholder: selectedValues.length === 0 || hideSelectedChips ? placeholder : undefined,
            warning: warning,
            children: [
                jsxs(Input.Field.Custom, {
                    "data-testid": "input-field",
                    onContainerClick: toggleMenu,
                    children: [
                        jsxs("div", {
                            className: "field-wrapper",
                            children: [
                                !selectAllOptionSelected && !hideSelectedChips && jsx(SelectedChips, {
                                    disabled: disabled,
                                    getSelectedItemLabel: getSelectedItemLabel,
                                    removeSelectedItem: toggleSelected,
                                    selectedItems: selectedItems,
                                    visibleSelectedCount: visibleSelectedCount
                                }),
                                showSelectAllOption && jsx("span", {
                                    children: selectAllOptionSelected?.label
                                }),
                                jsx(MultiSelectComboboxInner, {
                                    name: name,
                                    ...getInputProps({
                                        ...getDropdownProps({
                                            ref,
                                            preventKeyAction: isOpen
                                        }),
                                        // Must pass onChange here to avoid cursor jump
                                        onChange: handleInputChange
                                    })
                                })
                            ]
                        }),
                        showSelectAllOption && selectAllOptionSelected?.icon && jsx(Input.Inline.Icon, {
                            align: "left",
                            name: selectAllOptionSelected.icon
                        }),
                        !showSelectAllOption && display !== 'inline' && jsx(Input.Inline.Icon, {
                            align: "left",
                            name: "SearchOutline"
                        }),
                        jsx(Input.Inline.Icon, {
                            align: "right",
                            className: "caret",
                            name: "ChevronDownSolid",
                            size: 20
                        })
                    ]
                }),
                jsx("div", {
                    className: menuContainerCss,
                    "data-testid": "menu-container",
                    children: /*#__PURE__*/ React__default.isValidElement(children) && /*#__PURE__*/ React__default.cloneElement(children, {
                        onClearInput: handleClearInput,
                        getMenuProps,
                        getItemProps,
                        loading: menuLoading,
                        inputValue: internalInputValue,
                        selected: selectedItems,
                        disabled,
                        highlightedIndex
                    })
                })
            ]
        })
    });
}
const MultiSelectComboboxForwardRef = /*#__PURE__*/ forwardRef(MultiSelectComboboxCore);
MultiSelectComboboxForwardRef.displayName = 'Combobox';
MultiSelectComboboxForwardRef.Options = MultiSelectComboboxOptions;
MultiSelectComboboxForwardRef.stateChangeTypes = useCombobox.stateChangeTypes;
const MultiSelectCombobox = MultiSelectComboboxForwardRef;

export { MultiSelectCombobox, MultiSelectCombobox as default };
//# sourceMappingURL=MultiSelectCombobox.es.js.map
